import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay = 0 }) => (
    <span className="line-mask">
        <motion.span
            className="line-inner"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

export default function KineticHero({ hero }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

    // Prefer nullish coalescing so an admin who intentionally clears a field
    // sees the empty result on the site rather than silently reverting to a
    // hardcoded default.
    const titleLine1 = hero?.title_line1 ?? "The";
    const titleLine2 = hero?.title_line2 ?? "Equity";
    const titleLine3 = hero?.title_line3 ?? "Nation.";
    const tagline =
        hero?.tagline ??
        "A logical approach to economics for Canada — a manifesto for shared prosperity, presented in plain language.";
    const estLabel = hero?.est_label ?? "Est. 2025";
    const audienceLabel = hero?.audience_label ?? "For Canadians";
    // Hero image: prefer CMS-provided URL (uploaded via /admin > Home Copy)
    // but fall back to the shipped default so an empty string keeps working.
    const heroImage = hero?.image_url || "/assets/hero_globe.jpg";

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative w-full pt-24 md:pt-28 pb-2 md:pb-3 overflow-hidden"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-start relative">
                {/* Title */}
                <motion.h1
                    style={{ y: titleY }}
                    className="col-span-12 lg:col-span-8 font-display font-medium text-[color:var(--charcoal)] leading-[0.86] tracking-[-0.03em] text-[10.2vw] md:text-[7.14vw] lg:text-[6.12vw]"
                >
                    <MaskedLine delay={0.15}>
                        <span className="block">{titleLine1}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.28}>
                        <span className="block italic">{titleLine2}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.42}>
                        <span className="block">{titleLine3}</span>
                    </MaskedLine>
                </motion.h1>

                {/* Right column — tighter: tagline + meta line */}
                <div className="col-span-12 lg:col-span-4 flex flex-col gap-6 lg:pt-10 lg:pl-4">
                    <FadeUp delay={0.75}>
                        <p className="text-lg md:text-xl leading-[1.4] font-body text-[color:var(--charcoal)] max-w-md whitespace-pre-line">
                            {tagline}
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.9}>
                        <div className="flex items-center gap-4 pt-4 border-t border-[color:var(--charcoal)]/20">
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em]">
                                {estLabel}
                            </span>
                            <span className="tick-divider h-3" />
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em]">
                                {audienceLabel}
                            </span>
                        </div>
                    </FadeUp>
                </div>
            </div>

            {/* Framed hero image with parallax */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.4, ease: EASE, delay: 0.55 }}
                className="mx-auto max-w-[1440px] px-6 md:px-10 mt-4 md:mt-6 relative"
            >
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                    <div className="col-span-12 lg:col-span-10 lg:col-start-2">
                        <div className="hero-frame w-full">
                            <img
                                src={heroImage}
                                alt="Equity Nation hero"
                                className="block h-auto w-full"
                                draggable="false"
                            />
                            <div className="absolute left-4 md:left-6 bottom-4 md:bottom-6 z-10 text-white font-mono text-[12px] uppercase tracking-[0.24em] flex items-center gap-3">
                                <span className="inline-block h-1 w-8 bg-white" />
                                A Canadian Story
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

        </section>
    );
}
